import { h } from 'preact';

// Equine dental chart — modified Triadan numbering.
// Quadrants: 1xx = upper right, 2xx = upper left, 3xx = lower left, 4xx = lower right.
// Per quadrant: x01-x03 incisors, x04 canine ("tush"), x05 wolf tooth (vestigial PM1),
// x06-x08 premolars (PM2-PM4), x09-x11 molars (M1-M3). Full adult dentition = 44 positions.
// Layout follows the dog/cat convention: top-down "open mouth", animal's right = image left
// (quadrants 1 & 4 on the left, 2 & 3 on the right). The long vertical gap between the wolf
// teeth (x05) and the first cheek tooth (x06) represents the diastema ("the bars").
// Teeth a given horse lacks (e.g. a mare's canines, an absent wolf tooth) are recorded as
// the "missing" status, same as the dog/cat charts.

export const HORSE_VIEWBOX = '0 0 665 1080';

export const HORSE_TEETH = [
    // ---- Upper jaw (maxilla) ----
    // Incisors (front, top center): central (x01) flank the midline, corner (x03) outermost
    { id: '103', cx: 187, cy: 82, rx: 8, ry: 15, rot: -30, kind: 'incisor' },
    { id: '102', cx: 202, cy: 84, rx: 8, ry: 15, rot: -22, kind: 'incisor' },
    { id: '101', cx: 218, cy: 90, rx: 8, ry: 15, rot: -14, kind: 'incisor' },
    { id: '201', cx: 246, cy: 90, rx: 8, ry: 15, rot: 14, kind: 'incisor' },
    { id: '202', cx: 262, cy: 84, rx: 8, ry: 15, rot: 22, kind: 'incisor' },
    { id: '203', cx: 277, cy: 82, rx: 8, ry: 15, rot: 30, kind: 'incisor' },
    // Canines ("tush")
    { id: '104', cx: 162, cy: 140, rx: 9, ry: 16, rot: -18, kind: 'canine' },
    { id: '204', cx: 302, cy: 140, rx: 9, ry: 16, rot: 18, kind: 'canine' },
    // Wolf teeth (vestigial PM1)
    { id: '105', cx: 150, cy: 250, rx: 6, ry: 9, rot: -8, kind: 'premolar' },
    { id: '205', cx: 314, cy: 250, rx: 6, ry: 9, rot: 8, kind: 'premolar' },
    // Cheek teeth — left (quadrant 1): premolars PM2-PM4 then molars M1-M3
    { id: '106', cx: 142, cy: 300, rx: 13, ry: 22, rot: -6, kind: 'premolar' },
    { id: '107', cx: 136, cy: 348, rx: 14, ry: 23, rot: -5, kind: 'premolar' },
    { id: '108', cx: 132, cy: 397, rx: 14, ry: 23, rot: -4, kind: 'premolar' },
    { id: '109', cx: 130, cy: 446, rx: 14, ry: 23, rot: -3, kind: 'molar' },
    { id: '110', cx: 130, cy: 495, rx: 13, ry: 22, rot: 0, kind: 'molar' },
    { id: '111', cx: 132, cy: 542, rx: 11, ry: 20, rot: 2, kind: 'molar' },
    // Cheek teeth — right (quadrant 2)
    { id: '206', cx: 322, cy: 300, rx: 13, ry: 22, rot: 6, kind: 'premolar' },
    { id: '207', cx: 328, cy: 348, rx: 14, ry: 23, rot: 5, kind: 'premolar' },
    { id: '208', cx: 332, cy: 397, rx: 14, ry: 23, rot: 4, kind: 'premolar' },
    { id: '209', cx: 334, cy: 446, rx: 14, ry: 23, rot: 3, kind: 'molar' },
    { id: '210', cx: 334, cy: 495, rx: 13, ry: 22, rot: 0, kind: 'molar' },
    { id: '211', cx: 332, cy: 542, rx: 11, ry: 20, rot: -2, kind: 'molar' },

    // ---- Lower jaw (mandible) ----
    // Cheek teeth — left (quadrant 4): molars M3-M1 then premolars PM4-PM2 (caudal to rostral)
    { id: '411', cx: 132, cy: 600, rx: 11, ry: 20, rot: 2, kind: 'molar' },
    { id: '410', cx: 130, cy: 647, rx: 13, ry: 22, rot: 0, kind: 'molar' },
    { id: '409', cx: 130, cy: 695, rx: 14, ry: 23, rot: -3, kind: 'molar' },
    { id: '408', cx: 132, cy: 744, rx: 14, ry: 23, rot: -4, kind: 'premolar' },
    { id: '407', cx: 136, cy: 792, rx: 14, ry: 23, rot: -5, kind: 'premolar' },
    { id: '406', cx: 142, cy: 840, rx: 13, ry: 22, rot: -6, kind: 'premolar' },
    // Cheek teeth — right (quadrant 3)
    { id: '311', cx: 332, cy: 600, rx: 11, ry: 20, rot: -2, kind: 'molar' },
    { id: '310', cx: 334, cy: 647, rx: 13, ry: 22, rot: 0, kind: 'molar' },
    { id: '309', cx: 334, cy: 695, rx: 14, ry: 23, rot: 3, kind: 'molar' },
    { id: '308', cx: 332, cy: 744, rx: 14, ry: 23, rot: 4, kind: 'premolar' },
    { id: '307', cx: 328, cy: 792, rx: 14, ry: 23, rot: 5, kind: 'premolar' },
    { id: '306', cx: 322, cy: 840, rx: 13, ry: 22, rot: 6, kind: 'premolar' },
    // Wolf teeth (lower — uncommon, recorded as missing when absent)
    { id: '405', cx: 150, cy: 905, rx: 6, ry: 9, rot: 8, kind: 'premolar' },
    { id: '305', cx: 314, cy: 905, rx: 6, ry: 9, rot: -8, kind: 'premolar' },
    // Canines ("tush")
    { id: '404', cx: 162, cy: 965, rx: 9, ry: 16, rot: 18, kind: 'canine' },
    { id: '304', cx: 302, cy: 965, rx: 9, ry: 16, rot: -18, kind: 'canine' },
    // Incisors (bottom center)
    { id: '403', cx: 187, cy: 1023, rx: 8, ry: 15, rot: 30, kind: 'incisor' },
    { id: '402', cx: 202, cy: 1021, rx: 8, ry: 15, rot: 22, kind: 'incisor' },
    { id: '401', cx: 218, cy: 1015, rx: 8, ry: 15, rot: 14, kind: 'incisor' },
    { id: '301', cx: 246, cy: 1015, rx: 8, ry: 15, rot: -14, kind: 'incisor' },
    { id: '302', cx: 262, cy: 1021, rx: 8, ry: 15, rot: -22, kind: 'incisor' },
    { id: '303', cx: 277, cy: 1023, rx: 8, ry: 15, rot: -30, kind: 'incisor' },
];

export function HorseChartStatic() {
    return (
        <g>
            {/* Upper jaw (maxilla) outline */}
            <path class='dc-jaw' d='M 232 60 C 190 60 158 70 150 110 C 128 150 110 210 105 290 C 100 380 108 470 120 552 C 126 572 150 575 168 565 C 195 552 215 552 232 552 C 249 552 269 552 296 565 C 314 575 338 572 344 552 C 356 470 364 380 359 290 C 354 210 336 150 314 110 C 306 70 274 60 232 60 Z' />
            {/* Palatine raphe (midline detail) */}
            <path class='dc-detail' d='M 232 110 L 232 540' />
            {/* Lower jaw (mandible) outline */}
            <path class='dc-jaw' d='M 232 1035 C 190 1035 158 1025 150 985 C 128 945 110 885 105 805 C 100 715 108 625 120 543 C 126 523 150 520 168 530 C 195 543 215 543 232 543 C 249 543 269 543 296 530 C 314 520 338 523 344 543 C 356 625 364 715 359 805 C 354 885 336 945 314 985 C 306 1025 274 1035 232 1035 Z' />
            {/* Mandibular symphysis (midline detail) */}
            <path class='dc-detail' d='M 232 555 L 232 985' />
            <text class='dc-jaw-label' x='250' y='320'>Upper Jaw</text>
            <text class='dc-jaw-label' x='250' y='775'>Lower Jaw</text>
        </g>
    );
}

export function HorseChartLabels() {
    return (
        <g>
            {/* Upper incisors */}
            <text class='dc-label' x='178' y='40'>103</text>
            <line class='dc-leader' x1='188' y1='46' x2='188' y2='66' />
            <text class='dc-label' x='193' y='28'>102</text>
            <line class='dc-leader' x1='203' y1='34' x2='203' y2='68' />
            <text class='dc-label' x='209' y='40'>101</text>
            <line class='dc-leader' x1='219' y1='46' x2='219' y2='74' />
            <text class='dc-label' x='237' y='40'>201</text>
            <line class='dc-leader' x1='247' y1='46' x2='247' y2='74' />
            <text class='dc-label' x='253' y='28'>202</text>
            <line class='dc-leader' x1='263' y1='34' x2='263' y2='68' />
            <text class='dc-label' x='269' y='40'>203</text>
            <line class='dc-leader' x1='278' y1='46' x2='278' y2='66' />
            {/* Upper canines */}
            <text class='dc-label' x='88' y='145'>104</text>
            <line class='dc-leader' x1='116' y1='140' x2='151' y2='140' />
            <text class='dc-label' x='448' y='145'>204</text>
            <line class='dc-leader' x1='440' y1='140' x2='313' y2='140' />
            {/* Upper wolf teeth */}
            <text class='dc-label' x='88' y='255'>105</text>
            <line class='dc-leader' x1='116' y1='250' x2='144' y2='250' />
            <text class='dc-label' x='448' y='255'>205</text>
            <line class='dc-leader' x1='440' y1='250' x2='320' y2='250' />
            {/* Upper cheek teeth — left */}
            <text class='dc-label' x='20' y='305'>106</text>
            <line class='dc-leader' x1='48' y1='300' x2='128' y2='300' />
            <text class='dc-label' x='20' y='353'>107</text>
            <line class='dc-leader' x1='48' y1='348' x2='121' y2='348' />
            <text class='dc-label' x='20' y='402'>108</text>
            <line class='dc-leader' x1='48' y1='397' x2='117' y2='397' />
            <text class='dc-label' x='20' y='451'>109</text>
            <line class='dc-leader' x1='48' y1='446' x2='115' y2='446' />
            <text class='dc-label' x='20' y='500'>110</text>
            <line class='dc-leader' x1='48' y1='495' x2='116' y2='495' />
            <text class='dc-label' x='20' y='547'>111</text>
            <line class='dc-leader' x1='48' y1='542' x2='120' y2='542' />
            {/* Upper cheek teeth — right */}
            <text class='dc-label' x='448' y='305'>206</text>
            <line class='dc-leader' x1='440' y1='300' x2='336' y2='300' />
            <text class='dc-label' x='448' y='353'>207</text>
            <line class='dc-leader' x1='440' y1='348' x2='342' y2='348' />
            <text class='dc-label' x='448' y='402'>208</text>
            <line class='dc-leader' x1='440' y1='397' x2='346' y2='397' />
            <text class='dc-label' x='448' y='451'>209</text>
            <line class='dc-leader' x1='440' y1='446' x2='348' y2='446' />
            <text class='dc-label' x='448' y='500'>210</text>
            <line class='dc-leader' x1='440' y1='495' x2='347' y2='495' />
            <text class='dc-label' x='448' y='547'>211</text>
            <line class='dc-leader' x1='440' y1='542' x2='343' y2='542' />
            {/* Lower cheek teeth — left */}
            <text class='dc-label' x='20' y='605'>411</text>
            <line class='dc-leader' x1='48' y1='600' x2='120' y2='600' />
            <text class='dc-label' x='20' y='652'>410</text>
            <line class='dc-leader' x1='48' y1='647' x2='116' y2='647' />
            <text class='dc-label' x='20' y='700'>409</text>
            <line class='dc-leader' x1='48' y1='695' x2='115' y2='695' />
            <text class='dc-label' x='20' y='749'>408</text>
            <line class='dc-leader' x1='48' y1='744' x2='117' y2='744' />
            <text class='dc-label' x='20' y='797'>407</text>
            <line class='dc-leader' x1='48' y1='792' x2='121' y2='792' />
            <text class='dc-label' x='20' y='845'>406</text>
            <line class='dc-leader' x1='48' y1='840' x2='128' y2='840' />
            {/* Lower cheek teeth — right */}
            <text class='dc-label' x='448' y='605'>311</text>
            <line class='dc-leader' x1='440' y1='600' x2='343' y2='600' />
            <text class='dc-label' x='448' y='652'>310</text>
            <line class='dc-leader' x1='440' y1='647' x2='347' y2='647' />
            <text class='dc-label' x='448' y='700'>309</text>
            <line class='dc-leader' x1='440' y1='695' x2='348' y2='695' />
            <text class='dc-label' x='448' y='749'>308</text>
            <line class='dc-leader' x1='440' y1='744' x2='346' y2='744' />
            <text class='dc-label' x='448' y='797'>307</text>
            <line class='dc-leader' x1='440' y1='792' x2='342' y2='792' />
            <text class='dc-label' x='448' y='845'>306</text>
            <line class='dc-leader' x1='440' y1='840' x2='335' y2='840' />
            {/* Lower wolf teeth */}
            <text class='dc-label' x='88' y='910'>405</text>
            <line class='dc-leader' x1='116' y1='905' x2='144' y2='905' />
            <text class='dc-label' x='448' y='910'>305</text>
            <line class='dc-leader' x1='440' y1='905' x2='320' y2='905' />
            {/* Lower canines */}
            <text class='dc-label' x='88' y='970'>404</text>
            <line class='dc-leader' x1='116' y1='965' x2='151' y2='965' />
            <text class='dc-label' x='448' y='970'>304</text>
            <line class='dc-leader' x1='440' y1='965' x2='313' y2='965' />
            {/* Lower incisors */}
            <text class='dc-label' x='178' y='1072'>403</text>
            <line class='dc-leader' x1='188' y1='1066' x2='188' y2='1038' />
            <text class='dc-label' x='193' y='1058'>402</text>
            <line class='dc-leader' x1='203' y1='1052' x2='203' y2='1036' />
            <text class='dc-label' x='209' y='1072'>401</text>
            <line class='dc-leader' x1='219' y1='1066' x2='219' y2='1030' />
            <text class='dc-label' x='237' y='1072'>301</text>
            <line class='dc-leader' x1='247' y1='1066' x2='247' y2='1030' />
            <text class='dc-label' x='253' y='1058'>302</text>
            <line class='dc-leader' x1='263' y1='1052' x2='263' y2='1036' />
            <text class='dc-label' x='269' y='1072'>303</text>
            <line class='dc-leader' x1='278' y1='1066' x2='278' y2='1038' />
        </g>
    );
}
